import type { SurveyState } from '../surveys';

import { endOfDay, startOfDay } from 'date-fns';
import { pick } from 'lodash-es';
import { z } from 'zod';

import { searchSortingAlgorithms } from '../surveys/search-settings';
import { bigIntString } from './common';
import { packageExportOptions, packageFileTypes, uploadFileId } from './http/admin/io';

export const localisableMessage = z.object({
  key: z.string(),
  params: z.record(z.string(), z.union([z.string(), z.number()])).optional(),
});
export type LocalisableMessage = z.infer<typeof localisableMessage>;

export const repeatableBullJob = z.object({
  key: z.string(),
  name: z.string(),
  id: z.string().nullish(),
  limit: z.number().optional(),
  endDate: z.number().optional(),
  tz: z.string().optional(),
  pattern: z.string().optional(),
  every: z.number().optional(),
  next: z.number().optional(),
});

export type RepeatableBullJob = z.infer<typeof repeatableBullJob>;

export type JobData<T extends JobType = JobType, _R = void> = {
  type: T;
  params: JobParams[T];
  userId?: string | null;
};

export const redisStoreTypes = ['cache', 'rateLimiter', 'session'] as const;
export type RedisStoreType = (typeof redisStoreTypes)[number];

export const CleanRedisStore = z.object({
  store: z.enum(redisStoreTypes).array(),
});
export const CleanStorageFiles = z.record(z.string(), z.never());
export const FeedbackSchemesSync = z.record(z.string(), z.never());
export const LanguageTranslationsSync = z.record(z.string(), z.never());
export const LocaleIndexBuild = z.object({
  force: z.boolean().optional(),
});

export const localeCopyFoodsSubTasks = [
  'categories',
  'foods',
  'associatedFoods',
  'attributes',
  'brands',
  'foodBuilders',
  'splitLists',
  'splitWords',
  'standardUnits',
  'synonymSets',
] as const;
export type LocaleCopyFoodsSubTasks = (typeof localeCopyFoodsSubTasks)[number];
export const localeCopySystemSubTasks = ['searchPopularity', 'searchFixedRanking'] as const;
export type LocaleCopySystemSubTasks = (typeof localeCopySystemSubTasks)[number];
export const localeCopySubTasks = [...localeCopyFoodsSubTasks, ...localeCopySystemSubTasks] as const;
export type LocaleCopySubTasks = (typeof localeCopySubTasks)[number];
export const localesSyncSubTasks = ['foodBuilders'] as const;
export type LocaleSyncSubTasks = (typeof localesSyncSubTasks)[number];

export const LocaleCopy = z.object({
  localeId: bigIntString,
  sourceLocaleId: bigIntString,
  subTasks: z.enum(localeCopySubTasks).array(),
});
export const LocaleCategories = z.object({
  localeId: bigIntString,
});
export const LocaleFoods = z.object({
  localeId: bigIntString,
});
export const LocaleDeduplicateFoods = z.object({
  localeId: bigIntString,
  primaryCodes: z.array(z.string().nonempty()),
  dryRun: z.boolean().or(z.stringbool()).default(false),
});
export const LocaleFoodNutrientMapping = z.object({
  localeId: bigIntString,
});
export const LocaleFoodNutrientAssociationAssociate = z.object({
  localeId: bigIntString,
  mode: z.literal('associate'),
  file: z.string().nonempty(),
  dryRun: z.boolean().or(z.stringbool()).default(true),
});
export const LocaleFoodNutrientAssociationReplace = z.object({
  localeId: bigIntString,
  mode: z.literal('replace'),
  sourceNutrientTableId: z.string().nonempty(),
  targetNutrientTableId: z.string().nonempty(),
  dryRun: z.boolean().or(z.stringbool()).default(true),
});
export const LocaleFoodNutrientAssociation = z.discriminatedUnion('mode', [
  LocaleFoodNutrientAssociationAssociate,
  LocaleFoodNutrientAssociationReplace,
]);
export const LocaleFoodRankingUpload = z.object({
  localeId: bigIntString,
  file: z.string().nonempty(),
  targetAlgorithm: z.enum(searchSortingAlgorithms),
});
export const LocalesSync = z.object({
  subTasks: z.enum(localesSyncSubTasks).array(),
});
export const NutrientTableDataImport = z.object({
  nutrientTableId: z.string().nonempty(),
  file: z.string().nonempty(),
});
export const NutrientTableDataExport = z.object({
  nutrientTableId: z.string(),
});
export const NutrientTableMappingImport = z.object({
  nutrientTableId: z.string(),
  file: z.string().nonempty(),
});
export const NutrientTableMappingExport = z.object({
  nutrientTableId: z.string(),
});
export const PopularitySearchUpdateCounters = z.object({
  localeCode: z.string().nonempty(),
  foodCodes: z.array(z.string().nonempty()),
});
export const PurgeExpiredTokens = z.record(z.string(), z.never());

export const resources = [
  'as-served-sets',
  'as-served-sets.images',
  'drinkware-sets',
  'drinkware-sets.scales',
  'drinkware-sets.volumes',
  'guide-images',
  'guide-images.objects',
  'image-maps',
  'image-maps.objects',
  'languages',
  'locales',
  'nutrient-types',
  'standard-units',
  'surveys',
] as const;

export const ResourceExport = z.object({
  language: z.string().nonempty().array().optional(),
  resource: z.enum(resources),
});
export const SurveyAuthUrlsExport = z.object({
  surveyId: bigIntString,
});
export const SurveyDataExport = z.object({
  id: z.union([z.string(), z.array(z.string())]).optional(),
  surveyId: bigIntString,
  // TODO: Fix this - multiform data are not cleaned of empty strings
  startDate: z.union([
    z.string().length(0).transform(val => val || undefined),
    z.coerce.date(),
  ]).nullish().transform(val => val ? startOfDay(val) : undefined),
  endDate: z.union([
    z.string().length(0).transform(val => val || undefined),
    z.coerce.date(),
  ]).nullish().transform(val => val ? endOfDay(val) : undefined),
  userId: bigIntString.optional(),
});

const baseSurveyEventNotification = z.object({
  surveyId: bigIntString,
  userId: bigIntString, // TODO - Fix certain Task UIs to provide userId as string
  sessionId: z.uuid(),
});

export const SurveyEventNotification = z.discriminatedUnion('type', [
  baseSurveyEventNotification.extend({
    type: z.literal('survey.session.started'),
  }),
  baseSurveyEventNotification.extend({
    type: z.literal('survey.session.cancelled'),
  }),
  baseSurveyEventNotification.extend({
    type: z.literal('survey.session.submitted'),
    submissionId: z.uuid(),
  }),
]);
export const SurveyFeedbackNotification = z.object({
  surveyId: bigIntString,
  username: z.string(),
  submissions: z.array(z.string()).optional(),
  lang: z.string().optional(),
  to: z.string(),
  cc: z.string().optional(),
  bcc: z.string().optional(),
});
export const SurveyHelpRequestNotification = z.object({
  surveySlug: z.string(),
  userId: z.string(),
  name: z.string().nullish(),
  email: z.email().toLowerCase().nullish(),
  phone: z.string().nullish(),
  phoneCountry: z.string().nullish(),
  message: z.string().nullish(),
});
export const recalculationModes = ['none', 'values-only', 'values-and-codes'] as const;
export type RecalculationMode = (typeof recalculationModes)[number];

export const SurveyNutrientsRecalculation = z.object({
  surveyId: bigIntString,
  mode: z.enum(recalculationModes).default('values-only'),
  syncFields: z.boolean().or(z.stringbool()).default(false),
});
export const SurveyRatingsExport = z.object({
  surveyId: bigIntString,
});
export const SurveyRespondentsImport = z.object({
  surveyId: bigIntString,
  file: z.string().nonempty(),
});
export const SurveySchemesSync = z.object({});
export const SurveySessionsExport = z.object({
  surveyId: bigIntString,
});
export const SurveySubmission = z.object({
  surveyId: bigIntString,
  userId: bigIntString,
  // TODO: Fix this
  state: z.any(),
});
export const UserEmailVerificationNotification = z.object({
  email: z.email().toLowerCase(),
  userAgent: z.string().optional(),
});
export const UserPasswordResetNotification = z.object({
  email: z.email().toLowerCase(),
  userAgent: z.string().optional(),
});
export const PackageVerification = z.object({
  fileId: uploadFileId,
  packageFormat: z.string(),
});

export const PackageExport = packageExportOptions;

export const PackageImport = z.object({
  fileId: uploadFileId,
  verificationJobId: z.string(),
  options: z.object({
    conflictStrategies: z.partialRecord(z.enum(packageFileTypes), z.enum(['overwrite', 'skip', 'abort'])),
    include: z.enum(packageFileTypes).array(),
    localeFilter: z.array(z.string()).optional(),
    foodFilter: z.array(z.string()).optional(),
    categoryFilter: z.array(z.string()).optional(),
  }),
});

export const PackageConversionToXlsx = z.object({});

export const jobParams = z.object({
  CleanRedisStore,
  CleanStorageFiles,
  FeedbackSchemesSync,
  LanguageTranslationsSync,
  LocaleIndexBuild,
  LocaleCopy,
  LocaleCategories,
  LocaleFoods,
  LocaleDeduplicateFoods,
  LocaleFoodNutrientMapping,
  LocaleFoodNutrientAssociation,
  LocaleFoodRankingUpload,
  LocalesSync,
  NutrientTableDataImport,
  NutrientTableDataExport,
  NutrientTableMappingImport,
  NutrientTableMappingExport,
  PopularitySearchUpdateCounters,
  PurgeExpiredTokens,
  ResourceExport,
  SurveyAuthUrlsExport,
  SurveyDataExport,
  SurveyEventNotification,
  SurveyFeedbackNotification,
  SurveyHelpRequestNotification,
  SurveyNutrientsRecalculation,
  SurveyRatingsExport,
  SurveyRespondentsImport,
  SurveySchemesSync,
  SurveySessionsExport,
  SurveySubmission,
  UserEmailVerificationNotification,
  UserPasswordResetNotification,
  PackageVerification,
  PackageImport,
  PackageExport,
  PackageConversionToXlsx,
});
export type JobParams = z.infer<typeof jobParams>;

export const jobTypeParams = z.union([
  CleanRedisStore,
  CleanStorageFiles,
  FeedbackSchemesSync,
  LanguageTranslationsSync,
  LocaleIndexBuild,
  LocaleCopy,
  LocaleCategories,
  LocaleFoods,
  LocaleDeduplicateFoods,
  LocaleFoodNutrientMapping,
  LocaleFoodNutrientAssociation,
  LocaleFoodRankingUpload,
  LocalesSync,
  NutrientTableDataImport,
  NutrientTableDataExport,
  NutrientTableMappingImport,
  NutrientTableMappingExport,
  PopularitySearchUpdateCounters,
  PurgeExpiredTokens,
  ResourceExport,
  SurveyAuthUrlsExport,
  SurveyDataExport,
  SurveyEventNotification,
  SurveyFeedbackNotification,
  SurveyHelpRequestNotification,
  SurveyNutrientsRecalculation,
  SurveyRatingsExport,
  SurveyRespondentsImport,
  SurveySchemesSync,
  SurveySessionsExport,
  SurveySubmission,
  UserEmailVerificationNotification,
  UserPasswordResetNotification,
  PackageVerification,
  PackageImport,
  PackageExport,
]);
export type JobTypeParams = z.infer<typeof jobTypeParams>;

export const localeTasks = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('LocaleCopy'),
    params: LocaleCopy,
  }),
  z.object({
    type: z.literal('LocaleCategories'),
    params: LocaleCategories,
  }),
  z.object({
    type: z.literal('LocaleFoods'),
    params: LocaleFoods,
  }),
  z.object({
    type: z.literal('LocaleDeduplicateFoods'),
    params: LocaleDeduplicateFoods,
  }),
  z.object({
    type: z.literal('LocaleFoodNutrientMapping'),
    params: LocaleFoodNutrientMapping,
  }),
  z.object({
    type: z.literal('LocaleFoodNutrientAssociation'),
    params: z.discriminatedUnion('mode', [
      LocaleFoodNutrientAssociationAssociate.omit({ file: true }),
      LocaleFoodNutrientAssociationReplace,
    ]),
  }),
  z.object({
    type: z.literal('LocaleFoodRankingUpload'),
    params: LocaleFoodRankingUpload.omit({ file: true }),
  }),
]);
export type LocaleTask = z.infer<typeof localeTasks>;

export const localeJobs = [
  'LocaleCategories',
  'LocaleCopy',
  'LocaleFoods',
  'LocaleDeduplicateFoods',
  'LocaleFoodNutrientMapping',
  'LocaleFoodNutrientAssociation',
  'LocaleFoodRankingUpload',
] as const;
export type LocaleJob = (typeof localeJobs)[number];

export const nutrientTableTasks = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('NutrientTableDataImport'),
    params: NutrientTableDataImport.omit({ file: true }),
  }),
  z.object({
    type: z.literal('NutrientTableDataExport'),
    params: NutrientTableDataExport,
  }),
  z.object({
    type: z.literal('NutrientTableMappingImport'),
    params: NutrientTableMappingImport.omit({ file: true }),
  }),
  z.object({
    type: z.literal('NutrientTableMappingExport'),
    params: NutrientTableMappingExport,
  }),
]);
export type NutrientTableTask = z.infer<typeof nutrientTableTasks>;

export const nutrientTableJobs = ['NutrientTableMappingImport', 'NutrientTableMappingExport', 'NutrientTableDataImport', 'NutrientTableDataExport'] as const;
export type NutrientTableJob = (typeof nutrientTableJobs)[number];

export const surveyJobs = [
  'SurveyAuthUrlsExport',
  'SurveyDataExport',
  'SurveyNutrientsRecalculation',
  'SurveyRatingsExport',
  'SurveyRespondentsImport',
  'SurveySessionsExport',
] as const;
export type SurveyJob = (typeof surveyJobs)[number];

export const surveyTasks = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('SurveyAuthUrlsExport'),
    params: SurveyAuthUrlsExport,
  }),
  z.object({
    type: z.literal('SurveyDataExport'),
    params: SurveyDataExport,
  }),
  z.object({
    type: z.literal('SurveyNutrientsRecalculation'),
    params: SurveyNutrientsRecalculation,
  }),
  z.object({
    type: z.literal('SurveyRatingsExport'),
    params: SurveyRatingsExport,
  }),
  z.object({
    type: z.literal('SurveyRespondentsImport'),
    params: SurveyRespondentsImport.omit({ file: true }),
  }),
  z.object({
    type: z.literal('SurveySessionsExport'),
    params: SurveySessionsExport,
  }),
]);
export type SurveyTask = z.infer<typeof surveyTasks>;

export const userTasks = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('ResourceExport'),
    params: ResourceExport,
  }),
  z.object({
    type: z.literal('PackageVerification'),
    params: PackageVerification,
  }),
  z.object({
    type: z.literal('PackageImport'),
    params: PackageImport,
  }),
  z.object({
    type: z.literal('PackageExport'),
    params: PackageExport,
  }),
]);

export type UserTasks = z.infer<typeof userTasks>;

export const userJobs = [
  'ResourceExport',
] as const;
export type UserJob = (typeof userJobs)[number];

export const ioJobs = [
  'PackageVerification',
  'PackageImport',
  'PackageExport',
  'PackageConversionToXlsx',
] as const;
export type IOJob = (typeof ioJobs)[number];

export const jobTypes = [
  'CleanRedisStore',
  'CleanStorageFiles',
  'FeedbackSchemesSync',
  'LanguageTranslationsSync',
  'LocaleIndexBuild',
  'LocalesSync',
  'PopularitySearchUpdateCounters',
  'PurgeExpiredTokens',
  'ResourceExport',
  'SurveyEventNotification',
  'SurveyFeedbackNotification',
  'SurveyHelpRequestNotification',
  'SurveySchemesSync',
  'SurveySubmission',
  'UserEmailVerificationNotification',
  'UserPasswordResetNotification',
  ...localeJobs,
  ...nutrientTableJobs,
  ...surveyJobs,
  ...userJobs,
  ...ioJobs,
] as const;

export type JobType = keyof JobParams & (typeof jobTypes)[number];

export type GetJobParams<P extends keyof JobParams> = JobParams[P];

export type QueueJob<T extends JobType = JobType> = {
  userId: string;
  type: T;
  params: GetJobParams<T>;
};

export const defaultJobsParams: JobParams = {
  CleanRedisStore: { store: ['cache'] },
  CleanStorageFiles: {},
  FeedbackSchemesSync: {},
  LanguageTranslationsSync: {},
  LocaleIndexBuild: {
    force: false,
  },
  LocaleCopy: {
    localeId: '',
    sourceLocaleId: '',
    subTasks: [...localeCopySubTasks],
  },
  LocaleCategories: {
    localeId: '',
  },
  LocaleFoods: {
    localeId: '',
  },
  LocaleDeduplicateFoods: {
    localeId: '',
    primaryCodes: [],
    dryRun: false,
  },
  LocaleFoodNutrientMapping: {
    localeId: '',
  },
  LocaleFoodNutrientAssociation: {
    localeId: '',
    mode: 'associate',
    file: '',
    dryRun: false,
  },
  LocaleFoodRankingUpload: {
    localeId: '',
    file: '',
    targetAlgorithm: 'fixed',
  },
  LocalesSync: {
    subTasks: [...localesSyncSubTasks],
  },
  NutrientTableDataImport: {
    nutrientTableId: '',
    file: '',
  },
  NutrientTableDataExport: {
    nutrientTableId: '',
  },
  NutrientTableMappingImport: {
    nutrientTableId: '',
    file: '',
  },
  NutrientTableMappingExport: {
    nutrientTableId: '',
  },
  PopularitySearchUpdateCounters: {
    localeCode: '',
    foodCodes: [],
  },
  PurgeExpiredTokens: {},
  ResourceExport: {
    resource: 'nutrient-types',
  },
  SurveyAuthUrlsExport: {
    surveyId: '',
  },
  SurveyDataExport: {
    id: undefined,
    surveyId: '',
    startDate: undefined,
    endDate: undefined,
    userId: undefined,
  },
  SurveyEventNotification: {
    type: 'survey.session.started',
    surveyId: '',
    sessionId: '',
    userId: '',
  },
  SurveyNutrientsRecalculation: {
    surveyId: '',
    mode: 'values-only' as RecalculationMode,
    syncFields: false,
  },
  SurveyRatingsExport: {
    surveyId: '',
  },
  SurveyRespondentsImport: {
    surveyId: '',
    file: '',
  },
  SurveyHelpRequestNotification: {
    surveySlug: '',
    userId: '',
    name: '',
    email: '',
    phone: '',
    phoneCountry: 'GB',
    message: '',
  },
  SurveyFeedbackNotification: {
    surveyId: '',
    username: '',
    to: '',
  },
  SurveySchemesSync: {},
  SurveySessionsExport: {
    surveyId: '',
  },
  SurveySubmission: {
    surveyId: '',
    userId: '',
    state: {} as SurveyState,
  },
  UserEmailVerificationNotification: {
    email: '',
  },
  UserPasswordResetNotification: {
    email: '',
  },
  PackageVerification: {
    fileId: '',
    packageFormat: 'intake24',
  },
  PackageExport: {
    format: 'json',
    locales: ['en_GB'],
    options: {
      include: ['foods'],
    },
  },
  PackageImport: {
    fileId: '',
    verificationJobId: '',
    options: {
      include: [],
      conflictStrategies: {},
    },
  },
  PackageConversionToXlsx: {},
};

export const isValidJob = (job: any): boolean => jobTypes.includes(job);

export function pickJobParams<T extends keyof JobParams>(object: object, job: T): JobParams[T] {
  return pick(object, Object.keys(defaultJobsParams[job])) as JobParams[T];
}

export function jobRequiresFile<T extends keyof JobParams>(job: T, params?: object) {
  if (job === 'LocaleFoodNutrientAssociation')
    return (params as { mode?: unknown } | undefined)?.mode === 'associate';

  return Object.keys(defaultJobsParams[job]).includes('file');
}

export function jobHasParam<T extends keyof JobParams>(job: T, key: string) {
  return Object.keys(defaultJobsParams[job]).includes(key);
}
